import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { AlertTriangle, Clock, Copy, DollarSign, Gift, Link2, Percent, Shield, Ticket } from 'lucide-react';
import { PageLoadingIndicator } from '@kioskgaming/page-loading';
import { useTimezone, TimezonePicker, PlayerWithdrawalAutoApprovePanel } from '@kioskgaming/ui';
import AppLayout from '../components/layout/AppLayout';
import { useAuth } from '../hooks/useAuth';
import { fetchDashboard, fetchShopMe, fetchShopPlayerWithdrawalAutoApprove, generateShopInviteCode, patchPortalTwoFactor, patchShopLowBalanceWarning } from '../services/api';
import type { PlayerWithdrawalAutoApproveSnapshot } from '../services/api';
import { formatCostRate } from '../utils/costFormat';

const MIN_LOW_BALANCE_USD = 50;

export default function SettingsPage() {
  const { user, token, login } = useAuth();
  const { timezone } = useTimezone();
  const [loading, setLoading] = useState(true);
  const [lowBalEnabled, setLowBalEnabled] = useState(true);
  const [lowBalThreshold, setLowBalThreshold] = useState(String(MIN_LOW_BALANCE_USD));
  const [lowBalSaving, setLowBalSaving] = useState(false);
  const [twoFaEnabled, setTwoFaEnabled] = useState(false);
  const [twoFaChannel, setTwoFaChannel] = useState<'email' | 'phone'>('email');
  const [emailVerifiedFromApi, setEmailVerifiedFromApi] = useState(false);
  const [phoneVerifiedFromApi, setPhoneVerifiedFromApi] = useState(false);
  const [twoFaSaving, setTwoFaSaving] = useState(false);
  const [costRate, setCostRate] = useState<number | null>(null);
  const [inviteCode, setInviteCode] = useState<string | null>(null);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [inviteGenerating, setInviteGenerating] = useState(false);
  const [playerAutoApprove, setPlayerAutoApprove] = useState<PlayerWithdrawalAutoApproveSnapshot | null>(null);

  const emailVerified = !!(user?.emailVerified || emailVerifiedFromApi);
  const phoneVerified = !!(user?.phoneVerified || phoneVerifiedFromApi);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [res, meRes, thresholdRes] = await Promise.all([
          fetchDashboard(),
          fetchShopMe().catch(() => null),
          fetchShopPlayerWithdrawalAutoApprove().catch(() => null),
        ]);
        if (!cancelled && res.success && res.data) {
          setLowBalEnabled(res.data.low_balance_warning_enabled !== false);
          const t = res.data.low_balance_threshold_usd;
          if (t != null && Number.isFinite(Number(t))) {
            setLowBalThreshold(String(Number(t)));
          }
          setTwoFaEnabled(!!res.data.two_factor_enabled);
          setTwoFaChannel(res.data.two_factor_channel === 'phone' ? 'phone' : 'email');
          setEmailVerifiedFromApi(!!res.data.email_verified);
          setPhoneVerifiedFromApi(!!res.data.phone_verified);
          if (res.data.cost_rate != null && Number.isFinite(Number(res.data.cost_rate))) {
            setCostRate(Number(res.data.cost_rate));
          } else if (user?.costRate != null && Number.isFinite(user.costRate)) {
            setCostRate(user.costRate);
          }
        }
        if (!cancelled && meRes?.success && meRes.data?.shop) {
          const shop = meRes.data.shop as Record<string, unknown>;
          const code = (shop.inviteCode || shop.invite_code) as string | undefined;
          setInviteCode(code || null);
          setInviteLink((shop.inviteLink as string | undefined) || null);
        }
        if (!cancelled && thresholdRes?.success && thresholdRes.data) {
          setPlayerAutoApprove(thresholdRes.data);
        }
      } catch {
        if (!cancelled) toast.error('Could not load settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <AppLayout contentFullWidth>
      <h1 className="text-xl font-semibold tracking-tight text-stone-900 mb-6">Settings</h1>

      {loading ? (
        <PageLoadingIndicator variant="page" accentColor="#059669" />
      ) : (
        <>
        <div className="w-full rounded-xl border border-stone-200 bg-white p-5 shadow-sm sm:p-8">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-stone-100 text-stone-800">
              <Percent className="h-5 w-5" strokeWidth={2} aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-sm font-semibold text-stone-900">Cost rate</h2>
              <p className="mt-1 text-xs text-stone-600">
                Set by your Agent. Used when purchasing credits.
              </p>
              <p className="mt-3 text-2xl font-bold tabular-nums tracking-tight text-stone-900">
                {costRate != null ? formatCostRate(costRate) : '—'}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 w-full rounded-xl border border-stone-200 bg-white p-5 shadow-sm sm:p-8">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-800">
              <Ticket className="h-5 w-5" strokeWidth={2} aria-hidden />
            </div>
            <div className="min-w-0 flex-1 space-y-4">
              <div>
                <h2 className="text-sm font-semibold text-stone-900">
                  Telegram referral code (new accounts)
                </h2>
                <p className="mt-1 text-xs text-stone-600">
                  Referral code for new Telegram Mini App accounts only. Generate once, then share
                  the code or link so new Players can sign up under your Shop. Does not apply to
                  existing accounts or web sign-in. Cannot be changed after it is created.
                </p>
              </div>
              {inviteCode ? (
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <code className="rounded-lg bg-stone-100 px-3 py-2 text-sm font-semibold tracking-wider text-stone-900">
                      {inviteCode}
                    </code>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1.5 rounded-lg border border-stone-200 bg-white px-3 py-2 text-xs font-medium text-stone-800 hover:bg-stone-50"
                      onClick={() => {
                        void navigator.clipboard.writeText(inviteCode).then(
                          () => toast.success('Referral code copied'),
                          () => toast.error('Could not copy'),
                        );
                      }}
                    >
                      <Copy className="h-3.5 w-3.5" />
                      Copy code
                    </button>
                  </div>
                  {inviteLink ? (
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                      <p className="min-w-0 flex-1 truncate rounded-lg bg-stone-50 px-3 py-2 text-xs text-stone-700">
                        {inviteLink}
                      </p>
                      <button
                        type="button"
                        className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-stone-200 bg-white px-3 py-2 text-xs font-medium text-stone-800 hover:bg-stone-50"
                        onClick={() => {
                          void navigator.clipboard.writeText(inviteLink).then(
                            () => toast.success('Telegram referral link copied'),
                            () => toast.error('Could not copy'),
                          );
                        }}
                      >
                        <Link2 className="h-3.5 w-3.5" />
                        Copy link
                      </button>
                    </div>
                  ) : (
                    <p className="text-xs text-amber-800">
                      Link unavailable — bot username is not configured on the server.
                    </p>
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  disabled={inviteGenerating}
                  className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                  onClick={() => {
                    void (async () => {
                      setInviteGenerating(true);
                      try {
                        const res = await generateShopInviteCode();
                        if (res.success && res.data?.inviteCode) {
                          setInviteCode(res.data.inviteCode);
                          setInviteLink(res.data.inviteLink || null);
                          toast.success('Telegram referral code generated');
                        } else if (res.data?.inviteCode) {
                          setInviteCode(res.data.inviteCode);
                          setInviteLink(res.data.inviteLink || null);
                          toast.success('Referral code already exists');
                        } else {
                          toast.error(res.message || 'Could not generate referral code');
                        }
                      } catch (err: unknown) {
                        const ax = err as {
                          response?: { status?: number; data?: { message?: string; data?: { inviteCode?: string; inviteLink?: string | null } } };
                        };
                        if (ax.response?.status === 409 && ax.response.data?.data?.inviteCode) {
                          setInviteCode(ax.response.data.data.inviteCode);
                          setInviteLink(ax.response.data.data.inviteLink || null);
                          toast.success('Referral code already exists');
                        } else {
                          toast.error(ax.response?.data?.message || 'Could not generate referral code');
                        }
                      } finally {
                        setInviteGenerating(false);
                      }
                    })();
                  }}
                >
                    {inviteGenerating ? 'Generating…' : 'Generate Telegram referral code'}
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 w-full rounded-xl border border-stone-200 bg-white p-5 shadow-sm sm:p-8">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-stone-100 text-stone-800">
              <DollarSign className="h-5 w-5" strokeWidth={2} aria-hidden />
            </div>
            <PlayerWithdrawalAutoApprovePanel snapshot={playerAutoApprove} viewerLevel="shop" />
          </div>
        </div>

        <div className="mt-6 w-full rounded-xl border border-amber-200/90 bg-amber-50/40 p-5 shadow-sm ring-1 ring-amber-100 sm:p-8">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-800">
              <AlertTriangle className="h-5 w-5" strokeWidth={2} aria-hidden />
            </div>
            <div className="min-w-0 flex-1 space-y-4">
              <div>
                <h2 className="text-sm font-semibold text-stone-900">Low balance alerts</h2>
                <p className="mt-1 text-xs text-stone-600">
                  When your credit balance drops below the threshold, you will get a notification and an in-app
                  alert. Minimum threshold is ${MIN_LOW_BALANCE_USD} USD.
                </p>
              </div>
              <label className="flex cursor-pointer items-center gap-2 text-sm text-stone-800">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-stone-300 text-emerald-600 focus:ring-emerald-500"
                  checked={lowBalEnabled}
                  onChange={(e) => setLowBalEnabled(e.target.checked)}
                />
                Enable low balance warnings
              </label>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                <div className="flex-1">
                  <label htmlFor="shop-low-threshold" className="text-xs font-medium text-stone-600">
                    Warning threshold (USD)
                  </label>
                  <input
                    id="shop-low-threshold"
                    type="number"
                    min={MIN_LOW_BALANCE_USD}
                    step="0.01"
                    className="mt-1 w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm text-stone-900 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    value={lowBalThreshold}
                    onChange={(e) => setLowBalThreshold(e.target.value)}
                  />
                </div>
                <button
                  type="button"
                  disabled={lowBalSaving}
                  onClick={() => {
                    void (async () => {
                      const n = parseFloat(lowBalThreshold);
                      if (!Number.isFinite(n) || n < MIN_LOW_BALANCE_USD) {
                        toast.error(`Threshold must be at least ${MIN_LOW_BALANCE_USD} USD`);
                        return;
                      }
                      setLowBalSaving(true);
                      try {
                        const res = await patchShopLowBalanceWarning({
                          low_balance_warning_enabled: lowBalEnabled,
                          low_balance_threshold_usd: n,
                        });
                        if (res.success && res.data) {
                          toast.success('Settings saved');
                          setLowBalEnabled(res.data.low_balance_warning_enabled);
                          setLowBalThreshold(String(res.data.low_balance_threshold_usd));
                        } else {
                          toast.error(res.message || 'Could not save');
                        }
                      } catch {
                        toast.error('Could not save settings');
                      } finally {
                        setLowBalSaving(false);
                      }
                    })();
                  }}
                  className="inline-flex items-center justify-center rounded-lg bg-stone-800 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-stone-900 disabled:opacity-60"
                >
                  {lowBalSaving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 w-full rounded-xl border border-stone-200 bg-white p-5 shadow-sm sm:p-8">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-stone-100 text-stone-800">
              <Gift className="h-5 w-5" strokeWidth={2} aria-hidden />
            </div>
            <div className="min-w-0 flex-1 space-y-4">
              <div>
                <h2 className="text-sm font-semibold text-stone-900">Deposit bonus campaign</h2>
                <p className="mt-1 text-xs text-stone-600">
                  Reward your Players when they load credits: a percentage or a fixed amount, on every deposit or
                  the first one.
                </p>
              </div>
              <Link
                to="/deposit-bonus"
                className="inline-flex items-center justify-center rounded-lg bg-stone-800 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-stone-900"
              >
                Manage deposit bonus
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-6 w-full rounded-xl border border-stone-200 bg-white p-5 shadow-sm sm:p-8">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-stone-100 text-stone-800">
              <Clock className="h-5 w-5" strokeWidth={2} aria-hidden />
            </div>
            <div className="min-w-0 flex-1 space-y-3">
              <div>
                <h2 className="text-sm font-semibold text-stone-900">Timezone</h2>
                <p className="mt-1 text-xs text-stone-600">
                  All timestamps are stored in UTC. Choose your local timezone to display times correctly.
                  Current: <span className="font-medium text-stone-800">{timezone}</span>
                </p>
              </div>
              <TimezonePicker className="max-w-xs" />
            </div>
          </div>
        </div>

        <div className="mt-6 w-full rounded-xl border border-stone-200 bg-white p-5 shadow-sm sm:p-8">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-stone-100 text-stone-800">
              <Shield className="h-5 w-5" strokeWidth={2} aria-hidden />
            </div>
            <div className="min-w-0 flex-1 space-y-4">
              <div>
                <h2 className="text-sm font-semibold text-stone-900">Two-factor authentication</h2>
                <p className="mt-1 text-xs text-stone-600">
                  When enabled, sign-in requires a one-time code sent to your verified email or phone.
                </p>
              </div>
              {!emailVerified && !phoneVerified ? (
                <p className="text-xs text-amber-800">
                  Verify your email or phone before enabling 2FA.
                </p>
              ) : null}
              <label className="flex cursor-pointer items-center gap-2 text-sm text-stone-800">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-stone-300 text-emerald-600 focus:ring-emerald-500"
                  checked={twoFaEnabled}
                  disabled={!emailVerified && !phoneVerified}
                  onChange={(e) => setTwoFaEnabled(e.target.checked)}
                />
                Enable two-factor authentication
              </label>
              {twoFaEnabled && (emailVerified || phoneVerified) ? (
                <fieldset className="space-y-2">
                  <legend className="text-xs font-medium text-stone-600">Send codes via</legend>
                  {emailVerified ? (
                    <label className="flex items-center gap-2 text-sm text-stone-800">
                      <input
                        type="radio"
                        name="shop-two-fa-channel"
                        checked={twoFaChannel === 'email'}
                        onChange={() => setTwoFaChannel('email')}
                      />
                      Email
                    </label>
                  ) : null}
                  {phoneVerified ? (
                    <label className="flex items-center gap-2 text-sm text-stone-800">
                      <input
                        type="radio"
                        name="shop-two-fa-channel"
                        checked={twoFaChannel === 'phone'}
                        onChange={() => setTwoFaChannel('phone')}
                      />
                      SMS (phone)
                    </label>
                  ) : null}
                </fieldset>
              ) : null}
              <button
                type="button"
                disabled={twoFaSaving || (!emailVerified && !phoneVerified)}
                onClick={() => {
                  void (async () => {
                    setTwoFaSaving(true);
                    try {
                      const res = await patchPortalTwoFactor({
                        two_factor_enabled: twoFaEnabled,
                        ...(twoFaEnabled ? { two_factor_channel: twoFaChannel } : {}),
                      });
                      if (res.success && res.data) {
                        toast.success('Two-factor settings saved');
                        setTwoFaEnabled(!!res.data.two_factor_enabled);
                        if (res.data.two_factor_channel === 'phone') setTwoFaChannel('phone');
                        else if (res.data.two_factor_channel === 'email') setTwoFaChannel('email');
                        setEmailVerifiedFromApi(!!res.data.email_verified);
                        setPhoneVerifiedFromApi(!!res.data.phone_verified);
                        if (user && token) {
                          login(
                            {
                              ...user,
                              emailVerified: !!res.data.email_verified,
                              phoneVerified: !!res.data.phone_verified,
                            },
                            token
                          );
                        }
                      } else {
                        toast.error(res.message || 'Could not save');
                      }
                    } catch {
                      toast.error('Could not save two-factor settings');
                    } finally {
                      setTwoFaSaving(false);
                    }
                  })();
                }}
                className="inline-flex items-center justify-center rounded-lg bg-stone-800 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-stone-900 disabled:opacity-60"
              >
                {twoFaSaving ? 'Saving…' : 'Save 2FA settings'}
              </button>
            </div>
          </div>
        </div>
        </>
      )}
    </AppLayout>
  );
}
